import type { OfferDetail } from '@fos/shared/contracts/offers';
import { Send, ShieldCheck } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';

/**
 * „Wyślij do klienta” (modal 560 px; wzorzec Akceptacji z DESIGN §5): odbiorcy (kontakty klienta z emailem,
 * domyślnie główny), temat, wstęp i podgląd treści oferty w mailu. Osoba z „Wysyła maile” (i Akceptacjami do edycji)
 * klika „Wyślij” — zgoda zapisuje się przy wysyłce i oferta wychodzi od razu; inni „Wyślij do akceptacji”
 * (akceptacja „Wyślij ofertę”: zgoda dotyczy tej treści i tych odbiorców; zmiana oferty ją unieważnia).
 */
export function SendOfferDialog({
  offer,
  open,
  onOpenChange,
}: {
  offer: OfferDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const access = useAccess();
  // tylko etykieta (backend rozstrzyga: wynik `approvedNow`)
  const canSendNow = access.can('approvals', 'edit') && access.flag('sendsEmails');
  const options = trpc.offers.sendOptions.useQuery({ id: offer.id }, { enabled: open });
  const request = trpc.offers.requestSend.useMutation();
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ contactIds?: string; subject?: string; message?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);

  // wartości domyślne po otwarciu i wczytaniu opcji (odświeżenie z tymi samymi danymi nie zmienia referencji)
  useEffect(() => {
    if (!open || !options.data) return;
    setContactIds(options.data.defaultContactIds);
    setSubject(options.data.subject);
    setMessage(options.data.message);
    setErrors({});
    setFormError(null);
  }, [open, options.data]);

  const toggle = (id: string, checked: boolean) =>
    setContactIds((ids) => (checked ? [...new Set([...ids, id])] : ids.filter((x) => x !== id)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (contactIds.length === 0) {
      setErrors({ contactIds: 'Wybierz odbiorcę' });
      return;
    }
    if (!subject.trim()) {
      setErrors({ subject: 'Temat jest wymagany' });
      return;
    }
    try {
      const result = await request.mutateAsync({ id: offer.id, version: offer.version, contactIds, subject, message });
      toast.success(result.approvedNow ? 'Oferta wysłana' : 'Wysyłka czeka w Akceptacjach');
      await utils.offers.invalidate();
      onOpenChange(false);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const issues = validationIssues(err);
        const next: typeof errors = {};
        for (const i of issues) {
          if (i.path.startsWith('contactIds')) next.contactIds ??= i.message;
          else if (i.path === 'subject') next.subject ??= i.message;
          else if (i.path === 'message') next.message ??= i.message;
        }
        if (Object.keys(next).length > 0) {
          setErrors(next);
          return;
        }
      }
      setFormError(
        code === 'CONFLICT' && !errorMessage(err).includes('Akceptacjach')
          ? `${errorMessage(err)} Odśwież ofertę i spróbuj ponownie.`
          : errorMessage(err),
      );
    }
  };

  const data = options.data;
  const noContacts = data !== undefined && data.contacts.length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Wyślij ofertę do klienta"
      className={FULLSCREEN_ON_PHONE}
      description={`${offer.client.name} · wersja ${offer.currentVersion}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={request.isPending}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="send-offer-form"
            icon={Send}
            loading={request.isPending}
            disabled={!data || noContacts}
          >
            {canSendNow ? 'Wyślij' : 'Wyślij do akceptacji'}
          </Button>
        </>
      }
    >
      {options.isPending ? (
        <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : options.isError ? (
        <ErrorState compact onRetry={() => void options.refetch()} retrying={options.isFetching} />
      ) : (
        <form id="send-offer-form" onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Wysyłka">
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-caption font-medium text-fg-secondary">Odbiorcy</legend>
            {noContacts ? (
              <p className="text-body-s text-fg-secondary">
                Klient nie ma kontaktu z adresem email.{' '}
                <Link to={`/klienci/${offer.clientId}`} className="font-medium text-accent hover:underline">
                  Dodaj kontakt w profilu klienta
                </Link>
                .
              </p>
            ) : (
              options.data.contacts.map((c) => (
                <Checkbox
                  key={c.id}
                  checked={contactIds.includes(c.id)}
                  onCheckedChange={(checked) => toggle(c.id, checked)}
                  label={
                    <span>
                      <span className="font-medium text-fg">{c.name}</span>{' '}
                      <span className="text-fg-muted">{c.email}</span>
                      {c.isPrimary && <span className="text-fg-muted"> · główny</span>}
                    </span>
                  }
                />
              ))
            )}
            {errors.contactIds && (
              <p role="alert" className="text-caption text-danger">
                {errors.contactIds}
              </p>
            )}
          </fieldset>
          <Field label="Temat" error={errors.subject}>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Wstęp" error={errors.message} hint="Pod spodem dokleimy treść oferty i podpis.">
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
          {options.data.preview && (
            <details className="rounded-lg bg-surface px-3 py-2">
              <summary className="cursor-pointer text-body-s font-medium text-fg-secondary">
                <Label>Treść oferty w mailu</Label>
              </summary>
              <pre className="mt-2 max-h-60 overflow-auto font-sans text-caption whitespace-pre-wrap text-fg-secondary">
                {options.data.preview}
              </pre>
            </details>
          )}
          <p className="flex items-start gap-2 text-caption text-fg-muted">
            <ShieldCheck size={14} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
            {canSendNow
              ? 'Wyślemy od razu, a ta wersja zostanie zamrożona.'
              : 'Trafi do Akceptacji. Wyślemy po zatwierdzeniu treści i odbiorców, a ta wersja zostanie zamrożona. Zmiana oferty przed zatwierdzeniem unieważnia zgodę.'}
          </p>
          {formError && <FormMessage>{formError}</FormMessage>}
        </form>
      )}
    </Dialog>
  );
}
